import { ColorPicker } from "@/components/admin/content/ColorPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
    designUpdate,
    ThemeUpdateLayout,
    ThemeUpdateTextColorHeading,
} from "@/lib/api";
import { ITheme, IUserData, IUsersBrandAccounts } from "@/types";
import { toast } from "sonner";
import { masks } from "@/lib/imageMasks";

interface Props {
    user: IUserData;
    updateUser: IUserData;
    setUpdateUser: React.Dispatch<React.SetStateAction<IUserData>>;
    impersonate?: boolean;
    ws?: WebSocket | null;
    loggedInUser?: IUsersBrandAccounts | undefined;
}
export default function Customization({
    user,
    updateUser,
    setUpdateUser,
    impersonate,
    ws,
    loggedInUser,
}: Props) {
    const onLayoutChange = async (value: ITheme["layout"]) => {
        setUpdateUser({
            ...updateUser,
            theme: {
                ...updateUser.theme,
                layout: value,
            },
        });
        if (user.theme.layout === value) return;
        toast("Updating layout...");
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_header",
                    data: {
                        pic: updateUser.pic,
                        displayname: updateUser.displayname,
                        bio: updateUser.bio,
                        pronouns: updateUser.settings?.pronouns || "",
                        location: updateUser.settings.location,
                        layout: value,
                        design: updateUser.design,
                    },
                })
            );
        }
        let response: any = await ThemeUpdateLayout(user, value, impersonate);
        response = await response.json();
        if (!response.error) {
            // Update the user object directly
            user.theme.layout = value;
            toast.success("Updated");
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(response),
                }
            );
        }
    };
    const onDesignChange = async (
        value: IUserData["design"]["profile_pic_shape"]
    ) => {
        setUpdateUser({
            ...updateUser,
            design: {
                ...updateUser.design,
                profile_pic_shape: value,
            },
        });
        if (user.design.profile_pic_shape === value) return;
        toast("Updating Image Shape...");
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_header",
                    data: {
                        pic: updateUser.pic,
                        displayname: updateUser.displayname,
                        bio: updateUser.bio,
                        pronouns: updateUser.settings?.pronouns || "",
                        location: updateUser.settings.location,
                        layout: updateUser.theme.layout,
                        design: {
                            ...user.design,
                            profile_pic_shape: value,
                        },
                    },
                })
            );
        }
        let response: any = await designUpdate(
            user,
            {
                ...user.design,
                profile_pic_shape: value,
            },
            impersonate
        );
        response = await response.json();
        if (!response.error) {
            // Update the user object directly
            user.design.profile_pic_shape = value;
            toast.success("Updated");
        } else {
            toast.error(
                "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                {
                    description: JSON.stringify(response),
                }
            );
        }
    };
    return (
        <div className="flex flex-col gap-1">
            <Label variant="headingtoggle">Layout</Label>
            <div className="grid grid-cols-2 gap-2">
                <div
                    className={
                        "flex h-40 w-full flex-col items-center justify-between rounded-md border p-3 " +
                        `${updateUser.theme.layout == "default" ? "border-primary" : ""}`
                    }
                    onClick={() => {
                        onLayoutChange("default");
                    }}
                >
                    <Skeleton nopulse className="size-12 rounded-full" />
                    <Skeleton nopulse className="h-7 w-full rounded-md" />
                    <Skeleton nopulse className="h-7 w-full rounded-md" />
                </div>
                <div
                    className={
                        "flex h-40 w-full items-center justify-between gap-3 rounded-md border p-3 " +
                        `${updateUser.theme.layout == "sideways" ? "border-primary" : ""}`
                    }
                    onClick={() => {
                        onLayoutChange("sideways");
                    }}
                >
                    <Skeleton
                        nopulse
                        className="size-14 shrink-0 rounded-full"
                    />
                    <div className="flex w-full flex-col gap-3">
                        <Skeleton nopulse className="h-7 w-full rounded-md" />
                        <Skeleton nopulse className="h-7 w-full rounded-md" />
                    </div>
                </div>
            </div>
            {/* IMAGE SHAPE */}
            <Label variant="headingtoggle">Image shape</Label>
            <div className="flex flex-wrap gap-2">
                {Object.entries(masks).map(([key, value]) => (
                    <div
                        key={key}
                        className={
                            "flex h-16 w-16 place-content-center items-center rounded-md border p-3 " +
                            `${updateUser.design.profile_pic_shape === key ? "border-primary" : ""}`
                        }
                        onClick={() => {
                            onDesignChange(key);
                        }}
                    >
                        <Skeleton
                            nopulse
                            className="size-12 shrink-0"
                            //@ts-expect-error
                            style={{ WebkitMask: value, mask: value }}
                        />
                    </div>
                ))}
            </div>
            {/* <Separator />
            <Label variant="headingtoggle">Text</Label>
            <div className="flex items-center justify-between gap-4">
                <div className="">
                    <Label>Size</Label>
                </div>
                <Slider defaultValue={[2]} max={4} step={1} />
            </div>
            <Separator /> */}
        </div>
    );
}
